import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '~/common/stores';
import { globalErrorHandler } from '~/common/utils/error-handler.util';
import { signInApi, signOutApi, signUpApi } from '~/features/auth/api/auth.api';
import { fetchMe, resetAuthUser, setAuthUser } from '~/features/auth/stores/auth.slice';
import { resolveReturnTo, SIGN_IN_PATH } from '~/features/auth/utils/auth.util';

import type { SigninRequest, SignupRequest } from '~/features/auth/types/auth.type';

/**
 * 인증 훅 — Supabase Auth 기반.
 *
 * @description 로그인·가입·로그아웃과 세션 복원을 한곳에서 다룬다. 종전(enter-launcher-api) 대비
 *  사라진 것: 토큰 쿠키 관리, axios Authorization 주입, SMS 인증 게이트, 두 신원을 잇는 장치.
 *  Supabase 클라이언트가 세션을 자체 저장·갱신하므로 앱이 토큰을 직접 만질 이유가 없다.
 */
function useAuth() {
  const state = useAppSelector(state => state.auth);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  /** 저장된 세션에서 사용자 복원 — 새로고침·직접 진입 시 AuthGuard 가 호출한다. */
  const fetchAuthUser = () => dispatch(fetchMe());

  const handleSignIn = async (body: SigninRequest) => {
    try {
      const user = await signInApi(body);
      dispatch(setAuthUser(user));
      navigate(resolveReturnTo(searchParams.get('returnTo')));
      return true;
    } catch (err) {
      await globalErrorHandler(err, { title: '로그인 오류', disableAuthCheck: true });
      return false;
    }
  };

  /**
   * 회원가입 — 성공 시 **곧바로 로그인 상태**가 된다(이메일 확인 자동승인 설정).
   * @description 원본 시안은 가입 직후 반려견 등록(온보딩)으로 이어지므로 로그인 화면으로 되돌리지
   *  않고 온보딩으로 보낸다. 로그인 화면을 거치게 하면 방금 만든 계정을 다시 입력해야 한다.
   */
  const handleSignUp = async (body: SignupRequest) => {
    try {
      const user = await signUpApi(body);
      dispatch(setAuthUser(user));
      // 지도사에게는 온보딩(반려견 등록)이 없다 — 가입 직후 바로 자기 콘솔로 보낸다.
      navigate(body.role === 'tutor' ? '/tutor-console' : '/onboarding', { replace: true });
      return true;
    } catch (err) {
      await globalErrorHandler(err, { title: '회원가입 오류', disableAuthCheck: true });
      return false;
    }
  };

  const handleSignOut = async () => {
    await signOutApi();
    dispatch(resetAuthUser());
    navigate(SIGN_IN_PATH);
  };

  return {
    state,
    fetchAuthUser,
    handleSignIn,
    handleSignUp,
    handleSignOut,
  };
}

export default useAuth;
